'use strict';

const Storage = require('utils/storage');
const PersistenceConfig = require('utils/persistence-config');

const internals = {};

internals.defaultIdentity = () => `${process.env.TWILIO_ENV || 'test'}-nearpeer-1`;

internals.apiBaseUrl = () => {

    const apiHost = process.env.API_HOST || '';
    const apiPrefix = process.env.API_PREFIX || '/api';

    return `${apiHost}${apiPrefix}`.replace(/\/$/, '');
};

internals.request = async (path, options = {}) => {

    const response = await fetch(`${internals.apiBaseUrl()}${path}`, {
        ...options,
        headers: {
            'content-type': 'application/json',
            ...(options.headers || {})
        }
    });

    if (response.status === 204) {
        return null;
    }

    const payload = await response.json();

    if (!response.ok) {
        const error = new Error(payload.error || response.statusText || 'E2E Twilio request failed');
        error.code = payload.code;
        error.status = response.status;
        throw error;
    }

    return payload;
};

internals.http = {
    delete: (path) => internals.request(path, { method: 'DELETE' }),
    get: (path) => internals.request(path),
    patch: (path, payload) => internals.request(path, { method: 'PATCH', body: JSON.stringify(payload) }),
    post: (path, payload) => internals.request(path, { method: 'POST', body: JSON.stringify(payload) })
};

internals.createEmitter = () => {

    const listenersByEvent = new Map();

    return {
        emit: (event, payload) => {

            for (const listener of listenersByEvent.get(event) || []) {
                listener(payload);
            }
        },
        on: (event, listener) => {

            listenersByEvent.set(event, (listenersByEvent.get(event) || []).concat(listener));
        },
        removeAllListeners: (event) => {

            if (event) {
                listenersByEvent.delete(event);
                return;
            }

            listenersByEvent.clear();
        },
        removeListener: (event, listener) => {

            listenersByEvent.set(event, (listenersByEvent.get(event) || []).filter((candidate) => candidate !== listener));
        }
    };
};

internals.createPaginator = (items) => ({
    hasNextPage: false,
    hasPrevPage: false,
    items,
    nextPage: async () => internals.createPaginator([]),
    prevPage: async () => internals.createPaginator([])
});

internals.createMessage = ({ emitter, raw }) => {

    const message = {
        ...raw,
        attributes: raw.attributes || {},
        delivery: raw.delivery || {},
        state: { sid: raw.sid },
        updateBody: async (body) => {

            const { message: updatedRaw } = await internals.http.patch(
                `/__e2e__/twilio/conversations/${raw.conversation.sid}/messages/${raw.sid}`,
                { body, attributes: message.attributes }
            );
            const updated = internals.createMessage({ emitter, raw: updatedRaw });
            emitter.emit('messageUpdated', { message: updated });

            return updated;
        },
        updateAttributes: async (attributes) => {

            const { message: updatedRaw } = await internals.http.patch(
                `/__e2e__/twilio/conversations/${raw.conversation.sid}/messages/${raw.sid}`,
                { attributes }
            );
            const updated = internals.createMessage({ emitter, raw: updatedRaw });
            emitter.emit('messageUpdated', { message: updated });

            return updated;
        },
        remove: async () => {

            await internals.http.delete(`/__e2e__/twilio/conversations/${raw.conversation.sid}/messages/${raw.sid}`);
            emitter.emit('messageRemoved', message);

            return message;
        }
    };

    return message;
};

internals.createConversation = ({ emitter, raw, state }) => ({
    ...raw,
    getMessages: async (pageSize = 100) => {

        const { messages } = await internals.http.get(`/__e2e__/twilio/conversations/${raw.sid}/messages?order=desc&limit=${pageSize}`);

        return internals.createPaginator(messages.map((message) => internals.createMessage({ emitter, raw: message })));
    },
    sendMessage: async (body) => {

        const { message } = await internals.http.post(`/__e2e__/twilio/conversations/${raw.sid}/messages`, {
            author: state.identity,
            body,
            attributes: {}
        });
        const created = internals.createMessage({ emitter, raw: message });
        emitter.emit('messageAdded', created);

        return created.sid;
    },
    updateLastReadMessageIndex: async (lastReadMessageIndex) => {

        await internals.http.post(`/__e2e__/twilio/conversations/${raw.sid}/read`, { lastReadMessageIndex });

        return lastReadMessageIndex;
    }
});

internals.listSubscribedConversations = async ({ state }) => {

    const { conversations } = await internals.http.get(`/__e2e__/twilio/conversations?identity=${encodeURIComponent(state.identity)}`);

    return conversations || [];
};

internals.syncConversationEvents = async ({ emitter, state }) => {

    const conversations = await internals.listSubscribedConversations({ state });
    const nextKnown = new Map();

    for (const raw of conversations) {
        const serialized = JSON.stringify(raw);
        const previous = state.knownConversations.get(raw.sid);
        const conversation = internals.createConversation({ emitter, raw, state });

        nextKnown.set(raw.sid, serialized);

        if (!previous) {
            emitter.emit('conversationAdded', conversation);
            emitter.emit('conversationJoined', conversation);
            continue;
        }

        if (previous !== serialized) {
            emitter.emit('conversationUpdated', {
                conversation
            });
        }
    }

    for (const sid of state.knownConversations.keys()) {
        if (nextKnown.has(sid)) {
            continue;
        }

        const previousSerialized = state.knownConversations.get(sid);

        if (!previousSerialized) {
            continue;
        }

        const conversation = internals.createConversation({
            emitter,
            raw: JSON.parse(previousSerialized),
            state
        });

        emitter.emit('conversationRemoved', conversation);
        emitter.emit('conversationLeft', conversation);
    }

    state.knownConversations = nextKnown;
};

internals.startEventPolling = ({ emitter, state }) => {

    if (state.pollInterval) {
        return;
    }

    const poll = async () => {

        if (state.stopped) {
            return;
        }

        try {
            const { events, nextCursor } = await internals.http.get(`/__e2e__/twilio/events?identity=${encodeURIComponent(state.identity)}&cursor=${state.cursor}`);
            state.cursor = nextCursor;

            for (const event of events || []) {
                const message = internals.createMessage({ emitter, raw: event.message });
                emitter.emit(event.name, event.name === 'messageUpdated' ? { message } : message);
            }

            await internals.syncConversationEvents({ emitter, state });
        }
        catch (error) {
            // Keep polling. The API webServer can restart between UI sessions.
        }
    };

    Promise.all([
        internals.http.get(`/__e2e__/twilio/events?identity=${encodeURIComponent(state.identity)}&cursor=latest`),
        internals.listSubscribedConversations({ state })
    ])
        .then(([{ nextCursor }, conversations]) => {
            state.cursor = nextCursor;
            state.knownConversations = new Map(
                (conversations || []).map((conversation) => [conversation.sid, JSON.stringify(conversation)])
            );
        })
        .catch(() => null)
        .finally(() => {
            state.pollInterval = window.setInterval(poll, Number(Storage.getItem(PersistenceConfig.keys.e2e.twilioPollMs) || 350));
        });
};

exports.Client = {
    create: async (token) => {

        const emitter = internals.createEmitter();
        const state = {
            token,
            identity: Storage.getItem(PersistenceConfig.keys.e2e.twilioIdentity) || internals.defaultIdentity(),
            cursor: 'latest',
            knownConversations: new Map(),
            pollInterval: null,
            stopped: false
        };

        const hydrateConversation = (conversation) => internals.createConversation({ emitter, raw: conversation, state });
        internals.startEventPolling({ emitter, state });

        return {
            getConversationBySid: async (sid) => {

                const { conversation } = await internals.http.get(`/__e2e__/twilio/conversations/${sid}`);

                return hydrateConversation(conversation);
            },
            getConversationByUniqueName: async (uniqueName) => {

                const { conversations } = await internals.http.get('/__e2e__/twilio/conversations');
                const conversation = conversations.find((candidate) => candidate.uniqueName === uniqueName);

                if (!conversation) {
                    const error = new Error(`Conversation not found: ${uniqueName}`);
                    error.code = 20404;
                    throw error;
                }

                return hydrateConversation(conversation);
            },
            getSubscribedConversations: async () => {

                const { conversations } = await internals.http.get(`/__e2e__/twilio/conversations?identity=${encodeURIComponent(state.identity)}`);

                return internals.createPaginator(conversations.map(hydrateConversation));
            },
            on: emitter.on,
            removeAllListeners: emitter.removeAllListeners,
            removeListener: emitter.removeListener,
            shutdown: async () => {

                state.stopped = true;
                if (state.pollInterval) {
                    window.clearInterval(state.pollInterval);
                }
                emitter.removeAllListeners();
            },
            updateToken: async (nextToken) => {

                state.token = nextToken;
            }
        };
    }
};
